/*eslint-disable*/
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, Typography, CircularProgress } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import FastAPIClient from "../../client";
import config from "../../config";
import CustomBarChart from "./CustomBarChart"; // Import the CustomBarChart
import StatCard from "../LayoutComponents/StatCard";
import { BarChart } from "@mui/x-charts/BarChart";

const client = new FastAPIClient(config);

type UserCoverageLog = {
    sales_rep_name: string;
    team_name: string;
    warehouse_name: string;
    month: string;
    year: string;
    actual_meetings: number;
    expected_meetings: number;
    coverage: number;
};

interface UserCoverageProps {
    selectedUser: string;
    selectedTeam: string;
    selectedWarehouse: string;
    timePeriod: string;
}

const UserCoverage: React.FC<UserCoverageProps> = ({
    selectedUser,
    selectedTeam,
    selectedWarehouse,
    timePeriod,
}) => {
    const [data, setData] = useState<UserCoverageLog[] | null>(null);
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        dayjs().startOf("week"),
        dayjs().endOf("week"),
    ]);

    useEffect(() => {
        fetchCoverageData();
        console.log("chartData", chartData);
    }, []);

    const fetchCoverageData = async () => {
        try {
            const response = await client.fetchMonthlyCoverage(); // Replace with actual endpoint
            console.log("coverage", response.results);
            setData(response.results);
        } catch (error) {
            console.error("Error fetching coverage data:", error);
        }
    };

    // useEffect(() => {
    //     switch (timePeriod) {
    //         case "last7Days":
    //             setDateRange([
    //                 dayjs().subtract(6, "day").startOf("day"),
    //                 dayjs().endOf("day"),
    //             ]);
    //             break;
    //         case "last30Days":
    //             setDateRange([
    //                 dayjs().subtract(29, "day").startOf("day"),
    //                 dayjs().endOf("day"),
    //             ]);
    //             break;
    //         case "lastMonth":
    //             setDateRange([
    //                 dayjs().subtract(1, "month").startOf("month"),
    //                 dayjs().subtract(1, "month").endOf("month"),
    //             ]);
    //             break;
    //         default:
    //             setDateRange([dayjs().startOf("week"), dayjs().endOf("week")]);
    //             break;
    //     }
    // }, [timePeriod]);

    const filteredData = useMemo(() => {
        if (!data || !dateRange[0] || !dateRange[1]) return [];

        const [start, end] = dateRange;

        let filtered = data;

        // let filtered = data.filter((d) =>
        //     dayjs(d.month).isBetween(start, end, null, "[]")
        // );

        // if (selectedTeam !== "all") {
        //     filtered = filtered.filter((d) => d.team_name === selectedTeam);
        // }

        if (selectedUser !== "all") {
            filtered = filtered.filter(
                (d) => d.sales_rep_name === selectedUser
            );
        }

        if (selectedWarehouse !== "all") {
            filtered = filtered.filter(
                (d) => d.warehouse_name === selectedWarehouse
            );
        }

        return filtered;
    }, [data, selectedUser, selectedTeam, selectedWarehouse, dateRange]);

    const chartData = useMemo(() => {
        if (!filteredData || !dateRange[0] || !dateRange[1])
            return { labels: [], values: [], seriesLabels: [] };

        // Map to accumulate monthly totals
        const monthlyTotals = new Map<
            string,
            { actual_meetings: number; expected_meetings: number }
        >();

        filteredData.forEach((entry) => {
            const monthKey = `${entry.year}-${entry.month}`; // Create a unique key with year and month

            if (!monthlyTotals.has(monthKey)) {
                monthlyTotals.set(monthKey, {
                    actual_meetings: 0,
                    expected_meetings: 0,
                });
            }

            const totals = monthlyTotals.get(monthKey)!;
            totals.actual_meetings += entry.actual_meetings;
            totals.expected_meetings += entry.expected_meetings;
        });

        // Get unique months and sort them
        const labels = Array.from(monthlyTotals.keys()).sort();

        // For each month, compute overall coverage
        const values = labels.map((monthKey) => {
            const totals = monthlyTotals.get(monthKey)!;
            if (totals.expected_meetings > 0) {
                return (
                    (totals.actual_meetings / totals.expected_meetings) * 100
                );
            }
            return 0;
        });

        return {
            labels,
            values: [values],
            seriesLabels: ["Coverage"],
        };
    }, [filteredData, dateRange]);

    useEffect(() => {
        console.log("chartData", chartData);
    }, [chartData]);

    const overallCoverage = useMemo(() => {
        if (!filteredData.length) return 0;

        const totals = filteredData.reduce(
            (acc, entry) => {
                acc.actual += entry.actual_meetings;
                acc.expected += entry.expected_meetings;
                return acc;
            },
            { actual: 0, expected: 0 }
        );

        if (totals.expected === 0) return 0;

        return (totals.actual / totals.expected) * 100;
    }, [filteredData]);

    return (
        <StatCard title="Couverture clients" subtitle="" loading={!data}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div className="">
                    <BarChart
                        borderRadius={10}
                        hideLegend={true}
                        xAxis={[
                            {
                                data: chartData.labels,
                                scaleType: "band",
                                valueFormatter: (value: string) => {
                                    const [year, month] = value.split('-');
                                    const date = dayjs(`${year}-${month}-01`);
                                    const monthName = date.format("MMMM");
                                    const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
                                    return `${capitalizedMonth} ${year}`;
                                },
                            },
                        ]}
                        series={[
                            {
                                label: chartData.seriesLabels[0],
                                data: chartData.values[0], // this is your coverage array
                                stack: "stack1",
                                valueFormatter: (value) =>
                                    `${value.toFixed(0)}%`,
                            },
                        ]}
                        barLabel={(params) => `${params.value.toFixed(0)}%`}
                        margin={{
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                        }}
                        height={200}
                        grid={{ vertical: false, horizontal: true }}
                        sx={{
                            "& .MuiChartsAxis-left .MuiChartsAxis-line": {
                                display: "none",
                            },
                            "& .MuiChartsAxis-tickLabel span:first-of-type": {
                                fontWeight: "bold",
                            },
                            "& .MuiChartsAxis-bottom .MuiChartsAxis-line": {
                                display: "none",
                            },
                            "& .MuiChartsGrid-horizontalLine": {
                                stroke: "white",
                                strokeDasharray: "1 4",
                            },
                            "& .MuiChartsAxis-left .MuiChartsAxis-tickLabel": {
                                fill: "#000000", // Set label color
                                fontSize: 12,
                                fontFamily: "Roboto",
                            },

                            "& .MuiChartsAxis-bottom .MuiChartsAxis-tick": {
                                display: "none",
                            },
                            "& .MuiChartsAxis-left .MuiChartsAxis-tick": {
                                display: "none",
                            },
                            "& .MuiChartsLegend": {
                                display: "none", // This hides the legend
                            },
                        }}
                    />
                </div>
            </LocalizationProvider>
        </StatCard>
    );
};

export default UserCoverage;
